import { faEllipsisV, faExternalLink, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, memo, useContext } from 'react';

import { Color } from '@/constants/color';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonContext, { PersonContextInterface } from '@/pages/Person/PersonContext';
import Badge from '@/ui/components/Badge';
import Dropdown from '@/ui/components/Dropdown';
import Heading from '@/ui/components/Heading';
import ListCard from '@/ui/components/ListCard';
import { currencyFormatter } from '@/utils/currencies';
import { getDrugScreenResultTitle } from '@/utils/drugScreenResultTitle';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

import { Props } from './types';

const DrugScreenCard = ({ drugScreen, reviewLink, personTimeZone }: Props) => {
  const { navigateToDrugScreen } = useNavigateToPage();
  // PersonContext is absent on the standalone /appointments/:appointmentId route.
  const { person } = useContext(PersonContext) as Partial<PersonContextInterface>;
  const targetedSubstances = person?.targeted_substances;

  const {
    drug_screen_result_reason,
    id: drugScreenId,
    kind,
    occurred_at,
    payment_line_item,
    payout: { payout_in_cents },
    person: { id: personId },
    result,
    is_compliant
  } = drugScreen;

  const goToDrugScreen = () => navigateToDrugScreen(drugScreenId, personId);

  return (
    <ListCard
      debug={drugScreen}
      headingActions={
        <div className="flex items-center gap-x-4">
          {reviewLink && (
            <a
              className="text-gray-500 hover:text-gray-700 flex items-center"
              href={reviewLink}
              rel="noreferrer"
              target="_blank"
              title="Review drug screen in Homebase">
              <FontAwesomeIcon icon={faExternalLink} />
            </a>
          )}

          <Dropdown
            buttonText={<FontAwesomeIcon icon={faEllipsisV} />}
            customButtonClassName="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-700"
            items={[
              <button key="edit" onClick={goToDrugScreen} title="Edit">
                <FontAwesomeIcon icon={faPencil} /> Edit
                <span className="sr-only"> Drug Screen</span>
              </button>
            ]}
            showChevronIcon={false}
          />
        </div>
      }
      timestamp={{ datetime: occurred_at, time_zone: personTimeZone }}
      titleSensitive
      title={`${is_compliant ? '✅' : '❌'} ${getDrugScreenResultTitle(result, targetedSubstances)}`}>
      <Heading className="mb-1" value={5} text={`${snakeCaseToTitleCase(kind)}`} />

      {!!drug_screen_result_reason.length && (
        <ul className="mp-sensitive list-disc list-inside mb-1">
          {drug_screen_result_reason.map(({ id, name }) => {
            const suffixIdx = name.search(/\s?\(.+\)/);
            const correctedName = suffixIdx !== -1 ? name.slice(0, suffixIdx) : name;

            return (
              <li key={id} className="text-sm">
                {correctedName}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap gap-1">
        {payment_line_item.length ? (
          payment_line_item.map(({ money, id, status, type }) => (
            <Fragment key={id}>
              <Badge
                color={Color.GRAY}
                text={`Payment: ${snakeCaseToTitleCase(type)} ${money} (${status})`}
              />
            </Fragment>
          ))
        ) : (
          <Badge
            color={Color.GRAY}
            text={`Possible Reward: ${currencyFormatter(payout_in_cents)}`}
          />
        )}
      </div>
    </ListCard>
  );
};

export default memo(DrugScreenCard);
